import cn from 'classnames';
import React, { type ReactNode, useEffect, useRef, useState } from 'react';
import { Logo } from 'src/components/logo';
import { SnippetContent } from 'src/components/snippet-content';
import { Skeleton } from 'src/components/snippet-default/components/skeleton';
import { SnippetWrapper } from 'src/components/snippet-wrapper';
import { useAppContext } from 'src/context/store/app-provider';
import { useStyleConfig } from 'src/hooks/use-style-config';

import styles from './SnippetDefault.css';

const SINGLE_LINE_MAX_HEIGHT = 28;

interface Props {
  message: ReactNode;
  linkText: ReactNode;
  additionalContent?: ReactNode;
  onClick: () => void;
  classNameLink?: string;
  classNameText?: string;
}

export const SnippetDefault = ({
  message,
  linkText,
  additionalContent,
  onClick,
  classNameLink,
  classNameText,
}: Props) => {
  const { shouldInheritBg, loading, getExperiment } = useAppContext();
  const styleConfig = useStyleConfig();
  const borderLess = !styleConfig.hasBorder;
  const [isSingleLine, setSingleLine] = useState(false);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const isRamadanEnabled = getExperiment('CT2-836-ramadan-widgets') === 'test';

  const handleSnippetClick = () => {
    if (!loading) {
      onClick();
    }
  };

  useEffect(() => {
    if (!loading && contentRef.current) {
      const contentHeight = contentRef.current.offsetHeight;
      setSingleLine(contentHeight <= SINGLE_LINE_MAX_HEIGHT);
    }
  }, [contentRef, loading]);

  return (
    <SnippetWrapper
      onClick={handleSnippetClick}
      className={cn(styles.SnippetDefault, {
        [styles.SnippetDefault_borderless]: borderLess,
        [styles.SnippetDefault_backgroundWhite]: !shouldInheritBg,
      })}
    >
      <div
        className={cn(styles.SnippetDefault__Content, {
          [styles.SnippetDefault__Content_loading]: loading,
          [styles.SnippetDefault__Content_borderless]: borderLess,
          [styles.SnippetDefault__Content_singleLine]:
            isSingleLine && !isRamadanEnabled,
        })}
      >
        {loading ? (
          <Skeleton />
        ) : (
          <SnippetContent
            message={message}
            link={linkText}
            classNameText={classNameText}
            classNameLink={cn(styles.SnippetDefault__Link, classNameLink)}
            ref={contentRef}
          />
        )}

        <Logo
          className={styles.SnippetDefault__Logo}
          color={isRamadanEnabled ? 'ramadan' : 'black on green'}
        />
      </div>
      {!loading && additionalContent && (
        <div className={styles.SnippetDefault__AdditionalContent}>
          {additionalContent}
        </div>
      )}
    </SnippetWrapper>
  );
};
